import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

type AnalyticsValue = string | number | boolean | undefined;
type AnalyticsParams = Record<string, AnalyticsValue>;
const conversionEventNames: Record<string, string> = {
  donation: "donation_click",
  protest_details: "protest_details_click",
  petition: "petition_click",
  contact: "contact_click",
  social_media: "social_media_click",
  share: "share_click",
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();
const CONSENT_KEY = "tp-cookie-ack";

function hasAnalyticsConsent() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(CONSENT_KEY) === "accepted";
  } catch {
    return false;
  }
}

function currentPageDetails(): AnalyticsParams {
  if (typeof window === "undefined") return {};

  return {
    page_location: window.location.href,
    page_path: `${window.location.pathname}${window.location.search}`,
  };
}

export function initializeAnalytics() {
  if (!measurementId || typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = (...args: unknown[]) => {
      window.dataLayer?.push(args);
    };
    window.gtag("js", new Date());
    window.gtag("config", measurementId, { send_page_view: false });
  }

  if (!document.querySelector(`script[data-ga-measurement-id="${measurementId}"]`)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.dataset.gaMeasurementId = measurementId;
    document.head.appendChild(script);
  }

  return true;
}

export function trackPageView() {
  if (!measurementId || !hasAnalyticsConsent() || typeof window === "undefined") return;
  window.gtag?.("event", "page_view", currentPageDetails());
}

export function trackConversion(eventName: string, buttonLabel: string, extra: AnalyticsParams = {}) {
  if (!measurementId || !hasAnalyticsConsent() || typeof window === "undefined") return;
  window.gtag?.("event", conversionEventNames[eventName] ?? eventName, {
    ...currentPageDetails(),
    button_label: buttonLabel,
    ...extra,
  });
}

export function AnalyticsRouteTracking() {
  const location = useLocation();
  const [analyticsEnabled, setAnalyticsEnabled] = useState(hasAnalyticsConsent);

  useEffect(() => {
    const syncConsent = () => setAnalyticsEnabled(hasAnalyticsConsent());
    window.addEventListener("tp-cookie-consent", syncConsent);
    return () => window.removeEventListener("tp-cookie-consent", syncConsent);
  }, []);

  useEffect(() => {
    if (analyticsEnabled && initializeAnalytics()) trackPageView();
  }, [analyticsEnabled, location.pathname, location.search]);

  return null;
}